import { useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { futureBillablesDenialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import type { InvoiceTrackerPlan, InvoiceTrackerRow } from '@/lib/types';
import { Button } from '@/components/ui';
import { CountBadge } from '@/components/ui/CountBadge';
import { TextLink } from '@/components/ui/TextLink';
import { CheckIcon } from '@/components/catalog/icons';

/*
 * The Future Billables Tracker's Pending / Completed controls (owner
 * 2026-10-27): the two tab pills with their counts, the tick that marks a plan
 * Completed, the Reopen that undoes it, and the link to a raised document.
 *
 * ⚠️ **The pills are a copy of `TabToggle` in `EventInterestPage`**, the
 * Enquiries Tracker's tab strip, so the two trackers show counts identically:
 * `role="group"` + `aria-pressed`, `secondary` for the selected pill and `ghost`
 * for the other, a `CountBadge` after the label (which draws nothing for `null`
 * or `0`), and the count spoken in the button's accessible name. `max-w-full`
 * with NO `shrink-0` keeps the wrap live at 320.
 *
 * ⚠️ **Complete and Reopen are gated exactly like Edit** — on
 * `canWriteFutureBillables`, disabled with the reason when it is withheld, and
 * only on a standalone (plan) row. Complete has no confirmation: it is undone
 * from the Completed tab by Reopen.
 */

const PLAN_TABS: readonly { value: InvoiceTrackerPlan; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
];

export function PlanTabs({
  value,
  onChange,
  counts,
}: {
  value: InvoiceTrackerPlan;
  onChange: (next: InvoiceTrackerPlan) => void;
  /** `null` while unread or after a failed read: no badge, nothing in the name. */
  counts: Record<InvoiceTrackerPlan, number> | null;
}) {
  return (
    <div
      role="group"
      aria-label="Future billables status"
      className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5"
    >
      {PLAN_TABS.map((item) => {
        const count = counts === null ? null : counts[item.value];
        const hasCount = typeof count === 'number' && count > 0;
        return (
          <Button
            key={item.value}
            size="sm"
            variant={value === item.value ? 'secondary' : 'ghost'}
            aria-pressed={value === item.value}
            {...(hasCount ? { 'aria-label': `${item.label}, ${count}` } : {})}
            onClick={() => onChange(item.value)}
          >
            {item.label}
            <CountBadge count={count} />
          </Button>
        );
      })}
    </div>
  );
}

/** Lucide `rotate-ccw`, on the shared icon grid (24, stroke 1.75). */
function ReopenIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

/** Whether a quote or invoice has been raised from this row. */
function isRaisedRow(row: InvoiceTrackerRow): boolean {
  return row.invoiceId !== null || row.quoteId !== null;
}

/**
 * What a refused Complete / Reopen says, and whether the row has left the list
 * it was on regardless (it was already in the other tab, or it is gone).
 */
function refusalOf(
  caught: unknown,
  action: 'complete' | 'reopen',
  permissionReason: string | null,
): { message: string; rowGone: boolean } {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'FUTURE_BILLABLE_ALREADY_COMPLETED':
        return { message: 'This row is already marked Completed.', rowGone: true };
      case 'FUTURE_BILLABLE_NOT_COMPLETED':
        return { message: 'This row is no longer marked Completed.', rowGone: true };
      case 'FUTURE_BILLABLE_NOT_PLANNED':
        return {
          message: 'This row belongs to a quote or invoice, so it cannot be marked Completed.',
          rowGone: false,
        };
      case 'FUTURE_BILLABLE_RAISED':
        return {
          message: 'A quote or invoice has been raised from this row, so it cannot be reopened.',
          rowGone: false,
        };
      default:
        break;
    }
    if (caught.status === 404) {
      return { message: 'This row no longer exists.', rowGone: true };
    }
    if (caught.status === 403) {
      return {
        message:
          permissionReason ?? 'Your account no longer has permission to change planned documents.',
        rowGone: false,
      };
    }
    return { message: caught.message, rowGone: false };
  }
  return {
    message:
      caught instanceof Error
        ? caught.message
        : action === 'complete'
          ? 'Could not reach the server. The row was not marked Completed.'
          : 'Could not reach the server. The row was not reopened.',
    rowGone: false,
  };
}

interface PlanActionProps {
  row: InvoiceTrackerRow;
  disabled: boolean;
  /** The row left this tab's list (done, or already in the other tab / gone). */
  onRowGone: (key: string) => void;
  /** A refusal to show above the list; `null` clears the previous one. */
  onError: (message: string | null) => void;
}

function usePlanAction(
  { row, onRowGone, onError }: PlanActionProps,
  action: 'complete' | 'reopen',
) {
  const { ability } = useAuth();
  const [busy, setBusy] = useState(false);
  const canWrite = ability.canWriteFutureBillables;
  const reason = futureBillablesDenialReason(ability);
  const rowId = row.id;

  const run = async () => {
    if (rowId === null) return;
    setBusy(true);
    onError(null);
    try {
      if (action === 'complete') await api.completeFutureBillable(rowId);
      else await api.reopenFutureBillable(rowId);
      onRowGone(row.key);
    } catch (caught) {
      const refusal = refusalOf(caught, action, reason);
      onError(refusal.message);
      if (refusal.rowGone) onRowGone(row.key);
    } finally {
      setBusy(false);
    }
  };

  return { busy, canWrite, reason, run };
}

/** The tick on a Pending row. */
export function CompletePlannedAction(props: PlanActionProps) {
  const { row, disabled } = props;
  const { busy, canWrite, reason, run } = usePlanAction(props, 'complete');
  if (row.source !== 'standalone' || row.id === null || row.completedAt !== null) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      leadingIcon={<CheckIcon className="h-4 w-4 shrink-0" />}
      aria-label={`Mark completed: ${row.partyName}`}
      tooltip={
        canWrite ? 'Completed' : (reason ?? 'You do not have permission to change planned documents.')
      }
      disabled={!canWrite || disabled}
      loading={busy}
      onClick={() => void run()}
    />
  );
}

/** Reopen on a Completed row that was ticked by hand (never on a raised one). */
export function ReopenPlannedAction(props: PlanActionProps) {
  const { row, disabled } = props;
  const { busy, canWrite, reason, run } = usePlanAction(props, 'reopen');
  if (row.id === null || row.completedAt === null || isRaisedRow(row)) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      leadingIcon={<ReopenIcon />}
      aria-label={`Reopen: ${row.partyName}`}
      tooltip={
        canWrite ? 'Reopen' : (reason ?? 'You do not have permission to change planned documents.')
      }
      disabled={!canWrite || disabled}
      loading={busy}
      onClick={() => void run()}
    />
  );
}

/** The quote or invoice raised from a plan, as a real link. Nothing when none. */
export function RaisedDocumentLink({ row }: { row: InvoiceTrackerRow }) {
  const to =
    row.invoiceId !== null
      ? `/invoices/${row.invoiceId}`
      : row.quoteId !== null
        ? `/quotes/${row.quoteId}`
        : null;
  if (to === null) return null;
  const label = row.documentNumber ?? (row.invoiceId !== null ? 'Invoice' : 'Quote');
  return (
    <TextLink to={to} title={label} className="block max-w-full truncate text-sm">
      {label}
    </TextLink>
  );
}
